import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Grid, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Divider, Tabs, Tab, TextField,
} from '@mui/material'
import { Refresh, CheckCircle, Error as ErrorIcon, Tune, Payment, TrendingUp, Warning, Event } from '@mui/icons-material'
import {
  adminApi, tenantApi, scannerApi, analysisApi, analyticsApi,
  billingApi, kbApi, notificationApi, reportApi,
} from '../services/api'
import KBArticleReviewPanel from '../components/KBArticleReviewPanel'
import ProposedPatternsPanel from '../components/ProposedPatternsPanel'
import SystemParametersPanel from '../components/SystemParametersPanel'
import DemoRequestsPanel from '../components/DemoRequestsPanel'
import ACIATemplatesPanel from '../components/ACIATemplatesPanel'

const SERVICE_CHECKS = [
  { name: 'tenant', check: () => tenantApi.health() },
  { name: 'scanner', check: () => scannerApi.health() },
  { name: 'analysis', check: () => analysisApi.health() },
  { name: 'admin', check: () => adminApi.health() },
  { name: 'analytics', check: () => analyticsApi.health() },
  { name: 'billing', check: () => billingApi.health() },
  { name: 'knowledge-base', check: () => kbApi.health() },
  { name: 'notification', check: () => notificationApi.health() },
  { name: 'report', check: () => reportApi.health() },
]

const AdminPage = () => {
  const [tabValue, setTabValue] = useState(0)
  const [serviceHealth, setServiceHealth] = useState<Record<string, 'healthy' | 'unhealthy' | 'checking'>>({})
  const [alerts, setAlerts] = useState<any[]>([])
  const [auditLogs, setAuditLogs] = useState<any[]>([])
  const [tenants, setTenants] = useState<any[]>([])
  const [upgradeRequests, setUpgradeRequests] = useState<any[]>([])
  const [sendingLink, setSendingLink] = useState<string | null>(null)
  const [insights, setInsights] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchAll() }, [])

  const fetchAll = async () => {
    setLoading(true)
    // Check each service health directly (not via admin proxy)
    setServiceHealth(Object.fromEntries(SERVICE_CHECKS.map(s => [s.name, 'checking'])))
    const [healthResults, ...otherResults] = await Promise.all([
      Promise.allSettled(SERVICE_CHECKS.map(s => s.check())),
      adminApi.getAlerts().catch(() => ({ data: [] })),
      adminApi.getAuditLogs().catch(() => ({ data: [] })),
      tenantApi.getTenants().catch(() => ({ data: [] })),
      billingApi.getUpgradeRequests().catch(() => ({ data: [] })),
      tenantApi.getPlatformInsights().catch(() => ({ data: null })),
    ])
    setServiceHealth(Object.fromEntries(
      SERVICE_CHECKS.map((s, i) => [s.name, healthResults[i].status === 'fulfilled' ? 'healthy' : 'unhealthy'])
    ))
    const alertsData = otherResults[0] as any
    const logsData = otherResults[1] as any
    const tenantsData = otherResults[2] as any
    const upgradeData = otherResults[3] as any
    const insightsData = otherResults[4] as any
    setAlerts(alertsData.data?.alerts || alertsData.data || [])
    setAuditLogs(logsData.data?.logs || logsData.data || [])
    setTenants(tenantsData.data?.tenants || tenantsData.data || [])
    setUpgradeRequests(Array.isArray(upgradeData.data) ? upgradeData.data : [])
    setInsights(insightsData.data)
    setLoading(false)
  }

  // Compute platform stats from tenants
  const trialCount = tenants.filter(t => (t.tier || '').toLowerCase() === 'trial').length
  const activeCount = tenants.filter(t => (t.status || '').toLowerCase() === 'active').length
  const totalTenants = tenants.length

  const platformStats = [
    { label: 'Total Tenants', value: totalTenants },
    { label: 'Active', value: activeCount },
    { label: 'Trials', value: trialCount },
    { label: 'Services', value: SERVICE_CHECKS.length },
  ]

  const [couponId, setCouponId] = useState('')

  const handleSendPaymentLink = async (requestId: string) => {
    setSendingLink(requestId)
    try {
      const res = await billingApi.sendPaymentLink(requestId, couponId || undefined)
      alert(`Payment link sent! Checkout: ${res.data.checkout_url}`)
      setCouponId('')
      const refresh = await billingApi.getUpgradeRequests()
      setUpgradeRequests(Array.isArray(refresh.data) ? refresh.data : [])
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to send payment link')
    }
    setSendingLink(null)
  }

  const pendingUpgrades = upgradeRequests.filter(r => r.status === 'pending').length

  if (loading && tabValue === 0) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Platform Administration</Typography>
        {tabValue === 0 && (
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchAll}>Refresh</Button>
        )}
      </Box>

      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Overview" />
        <Tab label="Revenue Intelligence" icon={<TrendingUp />} iconPosition="start" />
        <Tab label={`Upgrade Requests${pendingUpgrades ? ` (${pendingUpgrades})` : ''}`} icon={<Payment />} iconPosition="start" />
        <Tab label="KB Article Review" icon={<Tune />} iconPosition="start" />
        <Tab label="Proposed Patterns" />
        <Tab label="Impact Analysis Templates" />
        <Tab label="System Parameters" />
        <Tab label="Cancellations" />
        <Tab label="Demos" icon={<Event />} iconPosition="start" />
      </Tabs>

      {/* Tab 0: Overview (existing content) */}
      {tabValue === 0 && (
        <Box>
          {/* Platform Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {platformStats.map((stat) => (
              <Grid item xs={6} sm={3} key={stat.label}>
                <Card><CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">{stat.value}</Typography>
                  <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                </CardContent></Card>
              </Grid>
            ))}
          </Grid>

          {/* Service Health */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Service Health</Typography>
              <Grid container spacing={1}>
                {SERVICE_CHECKS.map(({ name }) => {
                  const status = serviceHealth[name]
                  const isHealthy = status === 'healthy'
                  const isChecking = status === 'checking'
                  return (
                    <Grid item xs={6} sm={4} md={3} key={name}>
                      <Box sx={{
                        p: 1.5, borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1,
                        bgcolor: isHealthy ? 'success.light' : isChecking ? 'grey.100' : 'error.light',
                      }}>
                        {isHealthy ? <CheckCircle color="success" fontSize="small" /> :
                         isChecking ? <CircularProgress size={16} /> :
                         <ErrorIcon color="error" fontSize="small" />}
                        <Typography variant="body2" fontWeight={600}>{name}</Typography>
                      </Box>
                    </Grid>
                  )
                })}
              </Grid>
            </CardContent>
          </Card>

          <Grid container spacing={3}>
            {/* Active Alerts */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Alerts ({alerts.length})
                  </Typography>
                  {alerts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No active alerts</Typography>
                  ) : (
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {alerts.slice(0, 10).map((a: any, i: number) => (
                        <Box key={i} sx={{ p: 1, mb: 1, bgcolor: 'grey.50', borderRadius: 1, borderLeft: 3, borderColor: a.severity === 'critical' ? 'error.main' : 'warning.main' }}>
                          <Typography variant="subtitle2">{a.title || a.message}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.severity} | {new Date(a.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Audit Logs */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Recent Audit Logs</Typography>
                  {auditLogs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No audit logs</Typography>
                  ) : (
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {auditLogs.slice(0, 10).map((log: any, i: number) => (
                        <Box key={i} sx={{ p: 1, mb: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2">{log.action || log.message}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.user || 'system'} | {new Date(log.created_at || log.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Tab 1: Revenue Intelligence */}
      {tabValue === 1 && insights && (
        <Box>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Tenants', value: insights.summary?.total_tenants || 0, color: 'primary.main' },
              { label: 'Upsell Ready', value: insights.summary?.upsell_ready || 0, color: 'success.main' },
              { label: 'At Risk', value: insights.summary?.at_risk || 0, color: 'error.main' },
              { label: 'Agents Governed', value: insights.summary?.total_agents_governed || 0, color: 'info.main' },
              { label: 'Total Scans', value: insights.summary?.total_scans_platform || 0, color: 'secondary.main' },
            ].map((s) => (
              <Grid item xs={6} sm={2.4} key={s.label}>
                <Card><CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography variant="h4" color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent></Card>
              </Grid>
            ))}
          </Grid>

          {/* Tier Breakdown */}
          {insights.summary?.tier_breakdown && (
            <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
              {Object.entries(insights.summary.tier_breakdown).map(([tier, count]: any) => (
                <Chip key={tier} label={`${tier}: ${count}`} variant="outlined" />
              ))}
            </Box>
          )}

          {/* Tenant Table */}
          <Card>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tenant</TableCell>
                      <TableCell>Domain</TableCell>
                      <TableCell>Tier</TableCell>
                      <TableCell>Health</TableCell>
                      <TableCell>Scans (month)</TableCell>
                      <TableCell>Agents</TableCell>
                      <TableCell>Users</TableCell>
                      <TableCell>Signals</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(insights.tenants || []).map((t: any) => (
                      <TableRow key={t.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2">{t.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{t.contact_email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{t.email_domain || '-'}</Typography>
                          {t.auto_join_enabled === false && <Chip label="invite only" size="small" color="warning" sx={{ mt: 0.5 }} />}
                        </TableCell>
                        <TableCell><Chip label={t.tier} size="small" variant="outlined" /></TableCell>
                        <TableCell>
                          <Chip
                            label={t.health === 'upsell_ready' ? 'Upsell Ready' : t.health === 'at_risk' ? 'At Risk' : t.health === 'healthy' ? 'Healthy' : 'Inactive'}
                            size="small"
                            color={t.health === 'upsell_ready' ? 'success' : t.health === 'at_risk' ? 'error' : t.health === 'healthy' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{t.usage?.scans_this_month || 0}</Typography>
                          <Typography variant="caption" color={t.usage?.scan_quota_pct >= 70 ? 'error.main' : 'text.secondary'}>
                            {t.usage?.scan_quota_pct?.toFixed(0)}% of quota
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{t.usage?.agents || 0}</Typography>
                          <Typography variant="caption" color={t.usage?.agent_quota_pct >= 70 ? 'error.main' : 'text.secondary'}>
                            {t.usage?.agent_quota_pct?.toFixed(0)}% of quota
                          </Typography>
                        </TableCell>
                        <TableCell>{t.usage?.users || 0}</TableCell>
                        <TableCell sx={{ maxWidth: 250 }}>
                          {t.upsell_signals?.length > 0 && (
                            <Box sx={{ mb: 0.5 }}>
                              {t.upsell_signals.map((s: string, i: number) => (
                                <Chip key={i} label={s} size="small" color="success" variant="outlined" sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }} />
                              ))}
                            </Box>
                          )}
                          {t.churn_risks?.length > 0 && (
                            <Box>
                              {t.churn_risks.map((r: string, i: number) => (
                                <Chip key={i} label={r} size="small" color="error" variant="outlined" icon={<Warning sx={{ fontSize: 14 }} />} sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }} />
                              ))}
                            </Box>
                          )}
                          {t.trial_days_left !== null && t.trial_days_left !== undefined && (
                            <Typography variant="caption" color={t.trial_days_left <= 7 ? 'error.main' : 'text.secondary'}>
                              Trial: {t.trial_days_left}d left
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Tab 2: Upgrade Requests */}
      {tabValue === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Upgrade Requests</Typography>
            {upgradeRequests.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No upgrade requests</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tenant</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell>Cycle</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {upgradeRequests.map((req: any) => (
                      <TableRow key={req.id}>
                        <TableCell>{req.tenant_name || req.tenant_id}</TableCell>
                        <TableCell>{req.billing_email || '-'}</TableCell>
                        <TableCell>{req.plan_name}</TableCell>
                        <TableCell>{req.billing_cycle}</TableCell>
                        <TableCell>
                          <Chip
                            label={req.status}
                            size="small"
                            color={req.status === 'completed' ? 'success' : req.status === 'pending' ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{new Date(req.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {(req.status === 'pending' || req.status === 'payment_link_sent') && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                size="small"
                                placeholder="Coupon (optional)"
                                value={couponId}
                                onChange={e => setCouponId(e.target.value)}
                                sx={{ width: 140 }}
                              />
                              <Button
                                size="small"
                                variant="contained"
                                disabled={sendingLink === req.id}
                                onClick={() => handleSendPaymentLink(req.id)}
                              >
                                {sendingLink === req.id ? <CircularProgress size={16} /> :
                                 req.status === 'pending' ? 'Send Payment Link' : 'Resend Link'}
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 3: KB Article Review */}
      {tabValue === 3 && <KBArticleReviewPanel />}

      {/* Tab 4: Proposed Patterns */}
      {tabValue === 4 && <ProposedPatternsPanel />}

      {/* Tab 5: Impact Analysis Templates */}
      {tabValue === 5 && <ACIATemplatesPanel />}

      {/* Tab 6: System Parameters */}
      {tabValue === 6 && <SystemParametersPanel />}

      {/* Tab 7: Cancellations */}
      {tabValue === 7 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Cancellation Requests</Typography>
            <CancellationRequestsPanel />
          </CardContent>
        </Card>
      )}

      {/* Tab 8: Demos requested, scheduled and completed. Platform-only —
          every route the panel calls is gated on is_platform_admin. */}
      {tabValue === 8 && <DemoRequestsPanel />}
    </Box>
  )
}

const CancellationRequestsPanel = () => {
  const [requests, setRequests] = useState<any[]>([])
  const [processing, setProcessing] = useState<string | null>(null)

  useEffect(() => { fetchRequests() }, [])

  const fetchRequests = async () => {
    try {
      const res = await billingApi.getCancellationRequests()
      setRequests(Array.isArray(res.data) ? res.data : [])
    } catch { setRequests([]) }
  }

  const handleAction = async (id: string, action: 'approve' | 'decline') => {
    setProcessing(id)
    try {
      if (action === 'approve') await billingApi.approveCancellation(id)
      else await billingApi.declineCancellation(id)
      await fetchRequests()
    } catch (err: any) {
      alert(err.response?.data?.detail || `Failed to ${action}`)
    }
    setProcessing(null)
  }

  if (requests.length === 0) return <Typography variant="body2" color="text.secondary">No cancellation requests</Typography>

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Tenant</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Details</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((req: any) => (
            <TableRow key={req.id}>
              <TableCell>{req.tenant_name || req.tenant_id}</TableCell>
              <TableCell>{req.billing_email || '-'}</TableCell>
              <TableCell>{req.reason}</TableCell>
              <TableCell sx={{ maxWidth: 200 }}>
                <Typography variant="body2" noWrap>{req.reason_details || '-'}</Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={req.status}
                  size="small"
                  color={req.status === 'approved' ? 'error' : req.status === 'declined' ? 'success' : req.status === 'pending' ? 'warning' : 'default'}
                />
              </TableCell>
              <TableCell>{new Date(req.created_at).toLocaleDateString()}</TableCell>
              <TableCell>
                {req.status === 'pending' && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small" variant="contained" color="error"
                      disabled={processing === req.id}
                      onClick={() => handleAction(req.id, 'approve')}
                    >
                      {processing === req.id ? <CircularProgress size={16} /> : 'Approve'}
                    </Button>
                    <Button
                      size="small" variant="outlined"
                      disabled={processing === req.id}
                      onClick={() => handleAction(req.id, 'decline')}
                    >
                      Decline
                    </Button>
                  </Box>
                )}
                {req.status === 'approved' && req.period_end && (
                  <Typography variant="caption" color="text.secondary">
                    Ends: {new Date(req.period_end).toLocaleDateString()}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default AdminPage
